import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as schemas from '../../../assets/schemas/schemas';
import { ActionButton } from '../../common/Buttons/Buttons';
import { SearchInput } from '../../common/Forms/Input/Input.jsx';
import { VehicleTypeFormPanel } from './VehicleTypeFormPanel.jsx';
import { VehicleBrandFormPanel } from './VehicleBrandFormPanel.jsx';
import { VehicleModelFormPanel } from './VehicleModelFormPanel.jsx';
import {
  VehicularTypesTable,
  VehicularBrandsTable,
  VehicularModelsTable,
} from '../../common/Tables/Tables.jsx';
import {
  vehicularTypesTableHeader,
  vehicularBrandsTableHeader,
  vehicularModelsTableHeader,
} from '../../../assets/helpers/tables.js';
import { capitalize } from '../../../assets/helpers/funciones';
import { notify } from '../../../assets/helpers/toast';
import { vehicleService } from '../../../services/vehicleService.js';
import { itemsPerPageOptions, initialPager } from './parametersConstants.js';
import { inactiveRowCount } from './parametersTableUtils.js';
import { mapVehicleTypeFromApi } from './vehicleCatalogMappers.js';

export const VehicularParametersSection = () => {
  const [vehicleSubTab, setVehicleSubTab] = useState('tipos');

  const [vehicleTypesData, setVehicleTypesData] = useState({
    limit: initialPager.limit,
    page: 1,
    data: [],
    count: 0,
    isLoading: false,
  });
  const [vehicleTypesListVersion, setVehicleTypesListVersion] = useState(0);

  const [vehicleBrandsData, setVehicleBrandsData] = useState({
    limit: initialPager.limit,
    page: 1,
    data: [],
    count: 0,
    isLoading: false,
  });
  const [vehicleBrandsListVersion, setVehicleBrandsListVersion] = useState(0);
  const [vehicleBrandsForSelect, setVehicleBrandsForSelect] = useState([]);

  const [vehicleModelsData, setVehicleModelsData] = useState({
    limit: initialPager.limit,
    page: 1,
    data: [],
    count: 0,
    isLoading: false,
  });
  const [vehicleModelsListVersion, setVehicleModelsListVersion] = useState(0);

  const [vehicleTypesSearchInput, setVehicleTypesSearchInput] = useState('');
  const [vehicleTypesSearch, setVehicleTypesSearch] = useState('');
  const vehicleTypesSearchDebouncedRef = useRef('');

  const [vehicleBrandsSearchInput, setVehicleBrandsSearchInput] = useState('');
  const [vehicleBrandsSearch, setVehicleBrandsSearch] = useState('');
  const vehicleBrandsSearchDebouncedRef = useRef('');

  const [vehicleModelsSearchInput, setVehicleModelsSearchInput] = useState('');
  const [vehicleModelsSearch, setVehicleModelsSearch] = useState('');
  const vehicleModelsSearchDebouncedRef = useRef('');

  const [vehicleClassOptionsList, setVehicleClassOptionsList] = useState([]);

  const [filterTypesIds, setFilterTypesIds] = useState([]);
  const [filterClassesIds, setFilterClassesIds] = useState([]);
  const [typeOptionsForFilter, setTypeOptionsForFilter] = useState([]);
  const [, setTypeOptionsLoading] = useState(false);

  const [filterBrandsTabIds, setFilterBrandsTabIds] = useState([]);
  const [brandFilterOptions, setBrandFilterOptions] = useState([]);
  const [, setBrandFilterOptionsLoading] = useState(false);

  const [filterBrandIds, setFilterBrandIds] = useState([]);
  const [filterModelIds, setFilterModelIds] = useState([]);
  const [modelOptionsForFilter, setModelOptionsForFilter] = useState([]);
  const [, setModelOptionsLoading] = useState(false);

  const [editingVehicleTypeId, setEditingVehicleTypeId] = useState(null);
  const [showVehicleTypeForm, setShowVehicleTypeForm] = useState(false);
  const [showVehicleBrandForm, setShowVehicleBrandForm] = useState(false);
  const [editingVehicleBrandId, setEditingVehicleBrandId] = useState(null);
  const [showVehicleModelForm, setShowVehicleModelForm] = useState(false);
  const [editingVehicleModelId, setEditingVehicleModelId] = useState(null);

  const vehicleTypeValidationSchema = useMemo(
    () => schemas.createVehicleTypeSchema(!!editingVehicleTypeId),
    [editingVehicleTypeId]
  );

  const {
    register: registerVehicleType,
    handleSubmit: handleSubmitVehicleType,
    reset: resetVehicleTypeForm,
    formState: { errors: vehicleTypeErrors },
  } = useForm({
    resolver: yupResolver(vehicleTypeValidationSchema),
    defaultValues: {
      classId: '',
      description: '',
    },
  });

  const cancelEditVehicleType = useCallback(() => {
    setEditingVehicleTypeId(null);
    setShowVehicleTypeForm(false);
    resetVehicleTypeForm({ classId: '', description: '' });
  }, [resetVehicleTypeForm]);

  const openCreateVehicleTypeForm = useCallback(() => {
    setEditingVehicleTypeId(null);
    resetVehicleTypeForm({ classId: '', description: '' });
    setShowVehicleTypeForm(true);
  }, [resetVehicleTypeForm]);

  const handleEditVehicleType = useCallback(
    (row) => {
      setShowVehicleTypeForm(true);
      setEditingVehicleTypeId(row._id);
      resetVehicleTypeForm({
        classId: row.classId || '',
        description: row.type || row.name || '',
      });
    },
    [resetVehicleTypeForm]
  );

  const handleToggleVehicleTypeActive = useCallback(async (row) => {
    const next = !row.isActive;
    const response = await vehicleService.updateVehicleType(row._id, {
      isActive: next,
    });

    if ([200, 201].includes(response?.status)) {
      notify(next ? 'Tipo activado' : 'Tipo desactivado', 'success');
      setVehicleTypesListVersion((v) => v + 1);
    } else {
      notify(
        response?.message ??
        response?.data?.message ??
        'Error al actualizar el estado del tipo',
        'error'
      );
    }
  }, []);

  const vehicleBrandValidationSchema = useMemo(
    () => schemas.createVehicleBrandSchema(!!editingVehicleBrandId),
    [editingVehicleBrandId]
  );

  const {
    register: registerVehicleBrand,
    handleSubmit: handleSubmitVehicleBrand,
    reset: resetVehicleBrandForm,
    formState: { errors: vehicleBrandErrors },
  } = useForm({
    resolver: yupResolver(vehicleBrandValidationSchema),
    defaultValues: {
      classId: '',
      brandName: '',
    },
  });

  const cancelVehicleBrandForm = useCallback(() => {
    setEditingVehicleBrandId(null);
    setShowVehicleBrandForm(false);
    resetVehicleBrandForm({ classId: '', brandName: '' });
  }, [resetVehicleBrandForm]);

  const openCreateVehicleBrandForm = useCallback(() => {
    setEditingVehicleBrandId(null);
    resetVehicleBrandForm({ classId: '', brandName: '' });
    setShowVehicleBrandForm(true);
  }, [resetVehicleBrandForm]);

  const handleEditVehicleBrand = useCallback(
    (row) => {
      setShowVehicleBrandForm(true);
      setEditingVehicleBrandId(row._id);
      resetVehicleBrandForm({
        classId: row.classId || '',
        brandName: row.brand || '',
      });
    },
    [resetVehicleBrandForm]
  );

  const handleToggleVehicleBrandActive = useCallback(async (row) => {
    const next = !row.isActive;
    const response = await vehicleService.updateVehicleBrand(row._id, {
      isActive: next,
    });

    if ([200, 201].includes(response?.status)) {
      notify(next ? 'Marca activada' : 'Marca desactivada', 'success');
      setVehicleBrandsListVersion((v) => v + 1);
    } else {
      notify(
        response?.message ??
        response?.data?.message ??
        'Error al actualizar el estado de la marca',
        'error'
      );
    }
  }, []);

  const onSubmitVehicleBrandForm = handleSubmitVehicleBrand(async (data) => {
    if (editingVehicleBrandId) {
      const response = await vehicleService.updateVehicleBrand(
        editingVehicleBrandId,
        { name: data.brandName.trim() }
      );

      if ([200, 201].includes(response?.status)) {
        notify('Marca de vehículo actualizada', 'success');
        cancelVehicleBrandForm();
        setVehicleBrandsListVersion((v) => v + 1);
      } else {
        notify(
          response?.message ??
          response?.data?.message ??
          'Error al actualizar la marca de vehículo',
          'error'
        );
      }
      return;
    }

    const response = await vehicleService.createBrand({
      name: data.brandName.trim(),
      classesIds: data.classId ? [data.classId] : [],
    });

    if ([200, 201].includes(response?.status)) {
      notify('Marca de vehículo creada', 'success');
      resetVehicleBrandForm({ classId: '', brandName: '' });
      setShowVehicleBrandForm(false);
      setVehicleBrandsListVersion((v) => v + 1);
    } else {
      notify(
        response?.message ??
        response?.data?.message ??
        'Error al crear la marca de vehículo',
        'error'
      );
    }
  });

  const vehicleModelValidationSchema = useMemo(
    () => schemas.createVehicleModelSchema(!!editingVehicleModelId),
    [editingVehicleModelId]
  );

  const {
    register: registerVehicleModel,
    handleSubmit: handleSubmitVehicleModel,
    reset: resetVehicleModelForm,
    formState: { errors: vehicleModelErrors },
  } = useForm({
    resolver: yupResolver(vehicleModelValidationSchema),
    defaultValues: {
      brandId: '',
      modelName: '',
    },
  });

  const cancelVehicleModelForm = useCallback(() => {
    setEditingVehicleModelId(null);
    setShowVehicleModelForm(false);
    resetVehicleModelForm({ brandId: '', modelName: '' });
  }, [resetVehicleModelForm]);

  const openCreateVehicleModelForm = useCallback(() => {
    setEditingVehicleModelId(null);
    resetVehicleModelForm({ brandId: '', modelName: '' });
    setShowVehicleModelForm(true);
  }, [resetVehicleModelForm]);

  const handleEditVehicleModel = useCallback(
    (row) => {
      setShowVehicleModelForm(true);
      setEditingVehicleModelId(row._id);
      resetVehicleModelForm({
        brandId:
          (typeof row.brandId === 'object' && row.brandId
            ? row.brandId._id
            : row.brandId) || '',
        modelName: row.name || row.model || '',
      });
    },
    [resetVehicleModelForm]
  );

  const handleToggleVehicleModelActive = useCallback(async (row) => {
    const next = !row.isActive;
    const response = await vehicleService.updateVehicleModel(row._id, {
      isActive: next,
    });

    if ([200, 201].includes(response?.status)) {
      notify(next ? 'Modelo activado' : 'Modelo desactivado', 'success');
      setVehicleModelsListVersion((v) => v + 1);
    } else {
      notify(
        response?.message ??
        response?.data?.message ??
        'Error al actualizar el estado del modelo',
        'error'
      );
    }
  }, []);

  const onSubmitVehicleModelForm = handleSubmitVehicleModel(async (data) => {
    if (editingVehicleModelId) {
      const response = await vehicleService.updateVehicleModel(
        editingVehicleModelId,
        { name: data.modelName.trim() }
      );

      if ([200, 201].includes(response?.status)) {
        notify('Modelo de vehículo actualizado', 'success');
        cancelVehicleModelForm();
        setVehicleModelsListVersion((v) => v + 1);
      } else {
        notify(
          response?.message ??
          response?.data?.message ??
          'Error al actualizar el modelo de vehículo',
          'error'
        );
      }
      return;
    }

    const response = await vehicleService.createModel({
      brandId: data.brandId,
      name: data.modelName.trim(),
    });

    if ([200, 201].includes(response?.status)) {
      notify('Modelo de vehículo creado', 'success');
      resetVehicleModelForm({ brandId: '', modelName: '' });
      setShowVehicleModelForm(false);
      setVehicleModelsListVersion((v) => v + 1);
    } else {
      notify(
        response?.message ??
        response?.data?.message ??
        'Error al crear el modelo de vehículo',
        'error'
      );
    }
  });

  const onSubmitVehicleTypeForm = handleSubmitVehicleType(async (data) => {
    if (editingVehicleTypeId) {
      const response = await vehicleService.updateVehicleType(
        editingVehicleTypeId,
        { description: data.description.trim() }
      );

      if ([200, 201].includes(response?.status)) {
        notify('Tipo de vehículo actualizado', 'success');
        cancelEditVehicleType();
        setVehicleTypesListVersion((v) => v + 1);
      } else {
        notify(
          response?.message ??
          response?.data?.message ??
          'Error al actualizar el tipo de vehículo',
          'error'
        );
      }
      return;
    }

    const response = await vehicleService.createVehicleType({
      classId: data.classId,
      description: data.description.trim(),
    });

    if ([200, 201].includes(response?.status)) {
      notify('Tipo de vehículo creado', 'success');
      resetVehicleTypeForm({ classId: '', description: '' });
      setShowVehicleTypeForm(false);
      setVehicleTypesListVersion((v) => v + 1);
    } else {
      notify(
        response?.message ??
        response?.data?.message ??
        'Error al crear el tipo de vehículo',
        'error'
      );
    }
  });

  const vehicleBrandsTable = vehicleBrandsData;
  const vehicleModelsTable = vehicleModelsData;
  const inactiveVehicleTypes = inactiveRowCount(vehicleTypesData.data);
  const inactiveVehicleBrands = inactiveRowCount(vehicleBrandsData.data);
  const inactiveVehicleModels = inactiveRowCount(vehicleModelsData.data);

  const handleVehicleTypesPageChange = useCallback((_e, page) => {
    setVehicleTypesData((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const onVehicleTypesSelectPerPage = (event) => {
    const value = Number(event.target.value);
    setVehicleTypesData((prev) => ({
      ...prev,
      page: 1,
      limit: value,
    }));
  };
  const handleVehicleBrandsPageChange = useCallback((_e, page) => {
    setVehicleBrandsData((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const onVehicleBrandsSelectPerPage = (event) => {
    const value = Number(event.target.value);
    setVehicleBrandsData((prev) => ({
      ...prev,
      page: 1,
      limit: value,
    }));
  };
  const handleVehicleModelsPageChange = useCallback((_e, page) => {
    setVehicleModelsData((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const onVehicleModelsSelectPerPage = (event) => {
    const value = Number(event.target.value);
    setVehicleModelsData((prev) => ({
      ...prev,
      page: 1,
      limit: value,
    }));
  };

  const handleTypesIdsFilter = useCallback((ids) => {
    setFilterTypesIds(ids);
    setVehicleTypesData((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleClassesIdsFilter = useCallback((ids) => {
    setFilterClassesIds(ids);
    setVehicleTypesData((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleBrandsTabIdsFilter = useCallback((ids) => {
    setFilterBrandsTabIds(ids);
    setVehicleBrandsData((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleBrandIdsFilter = useCallback((ids) => {
    setFilterBrandIds(ids);
    setVehicleModelsData((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleModelIdsFilter = useCallback((ids) => {
    setFilterModelIds(ids);
    setVehicleModelsData((prev) => ({ ...prev, page: 1 }));
  }, []);

  const vehicularTypesHeaderWithFilter = useMemo(
    () =>
      vehicularTypesTableHeader.map((h) => {
        if (h?.value === 'name') {
          return {
            ...h,
            sortable: true,
            filterOptions: typeOptionsForFilter,
            filterSearch: handleTypesIdsFilter,
          };
        }
        if (h?.value === 'vehicleClass') {
          return {
            ...h,
            sortable: true,
            filterOptions: vehicleClassOptionsList,
            filterSearch: handleClassesIdsFilter,
          };
        }
        return h;
      }),
    [
      typeOptionsForFilter,
      handleTypesIdsFilter,
      vehicleClassOptionsList,
      handleClassesIdsFilter,
    ]
  );

  const vehicularBrandsHeaderWithFilter = useMemo(
    () =>
      vehicularBrandsTableHeader.map((h) =>
        h?.value === 'name'
          ? {
              ...h,
              sortable: true,
              filterOptions: brandFilterOptions,
              filterSearch: handleBrandsTabIdsFilter,
            }
          : h
      ),
    [brandFilterOptions, handleBrandsTabIdsFilter]
  );

  const vehicularModelsHeaderWithFilter = useMemo(
    () =>
      vehicularModelsTableHeader.map((h) => {
        if (h?.value === 'name') {
          return {
            ...h,
            sortable: true,
            filterOptions: modelOptionsForFilter,
            filterSearch: handleModelIdsFilter,
          };
        }
        if (h?.value === 'brandName') {
          return {
            ...h,
            sortable: true,
            filterOptions: vehicleBrandsForSelect.map((b) => ({
              _id: b._id,
              name: capitalize(b.name ?? b.brand ?? ''),
            })),
            filterSearch: handleBrandIdsFilter,
          };
        }
        return h;
      }),
    [
      modelOptionsForFilter,
      handleModelIdsFilter,
      vehicleBrandsForSelect,
      handleBrandIdsFilter,
    ]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = vehicleTypesSearchInput.trim();
      if (trimmed === vehicleTypesSearchDebouncedRef.current) return;
      vehicleTypesSearchDebouncedRef.current = trimmed;
      setVehicleTypesSearch(trimmed);
      setVehicleTypesData((prev) => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(t);
  }, [vehicleTypesSearchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = vehicleBrandsSearchInput.trim();
      if (trimmed === vehicleBrandsSearchDebouncedRef.current) return;
      vehicleBrandsSearchDebouncedRef.current = trimmed;
      setVehicleBrandsSearch(trimmed);
      setVehicleBrandsData((prev) => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(t);
  }, [vehicleBrandsSearchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = vehicleModelsSearchInput.trim();
      if (trimmed === vehicleModelsSearchDebouncedRef.current) return;
      vehicleModelsSearchDebouncedRef.current = trimmed;
      setVehicleModelsSearch(trimmed);
      setVehicleModelsData((prev) => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(t);
  }, [vehicleModelsSearchInput]);

  const vehicleBrandSelectOptionsForModels = useMemo(() => {
    const placeholder = { _id: '', name: 'Seleccionar marca...' };
    const brands = Array.isArray(vehicleBrandsForSelect)
      ? vehicleBrandsForSelect
      : [];
    return [
      placeholder,
      ...brands.map((b) => ({
        _id: b._id,
        name: capitalize(b.name ?? b.brand ?? ''),
      })),
    ];
  }, [vehicleBrandsForSelect]);

  useEffect(() => {
    if (vehicleSubTab !== 'tipos') {
      setShowVehicleTypeForm(false);
      setEditingVehicleTypeId(null);
      resetVehicleTypeForm({ classId: '', description: '' });
    }
    if (vehicleSubTab !== 'marcas') {
      setShowVehicleBrandForm(false);
      setEditingVehicleBrandId(null);
      resetVehicleBrandForm({ classId: '', brandName: '' });
    }
    if (vehicleSubTab !== 'modelos') {
      setShowVehicleModelForm(false);
      setEditingVehicleModelId(null);
      resetVehicleModelForm({ brandId: '', modelName: '' });
    }

    // Clear all vehicular table filters when switching sub-tabs.
    setFilterTypesIds([]);
    setFilterClassesIds([]);
    setFilterBrandsTabIds([]);
    setFilterBrandIds([]);
    setFilterModelIds([]);

    // Reset pagination to first page on tab switch.
    setVehicleTypesData((prev) => ({ ...prev, page: 1 }));
    setVehicleBrandsData((prev) => ({ ...prev, page: 1 }));
    setVehicleModelsData((prev) => ({ ...prev, page: 1 }));
  }, [
    vehicleSubTab,
    resetVehicleTypeForm,
    resetVehicleBrandForm,
    resetVehicleModelForm,
  ]);

  useEffect(() => {
    let cancelled = false;
    vehicleService
      .getSalesVehicleClasses()
      .then((response) => {
        if (cancelled) return;
        if (response.status === 200) {
          setVehicleClassOptionsList(response?.data?.classes);
        } else {
          notify(
            response?.message ?? 'Error al cargar clases de vehículo',
            'error'
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          notify('Error al cargar clases de vehículo', 'error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTypeOptionsLoading(true);
    vehicleService
      .listVehicleTypesByFilter({ page: 1, limit: 100000, typesIds: [], classesIds: [] })
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          const raw = response?.data?.data ?? [];
          setTypeOptionsForFilter(
            raw.map((t) => ({ _id: t._id, name: t.type ?? t.description ?? '' }))
          );
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTypeOptionsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (vehicleSubTab !== 'modelos') return undefined;
    let cancelled = false;
    setModelOptionsLoading(true);
    vehicleService
      .listVehicleModelsByFilter({ page: 1, limit: 100000, brandIds: [], modelIds: [] })
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          const raw = response?.data?.data ?? response?.data?.vehicleModels ?? [];
          setModelOptionsForFilter(
            (Array.isArray(raw) ? raw : []).map((m) => ({
              _id: m._id,
              name: capitalize(m.name ?? m.model ?? ''),
            }))
          );
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setModelOptionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [vehicleSubTab, vehicleModelsListVersion]);

  useEffect(() => {
    if (vehicleSubTab !== 'marcas') return undefined;
    let cancelled = false;
    setBrandFilterOptionsLoading(true);
    vehicleService
      .listVehicleBrandsByFilter({ page: 1, limit: 100000, brandIds: [] })
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          const raw = response?.data?.data ?? response?.data?.vehicleBrands ?? [];
          setBrandFilterOptions(
            (Array.isArray(raw) ? raw : []).map((b) => ({
              _id: b._id,
              name: capitalize(b.name ?? b.brand ?? ''),
            }))
          );
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setBrandFilterOptionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [vehicleSubTab, vehicleBrandsListVersion]);

  useEffect(() => {
    if (vehicleSubTab !== 'marcas') {
      return undefined;
    }

    let cancelled = false;

    setVehicleBrandsData((prev) => ({ ...prev, isLoading: true }));

    vehicleService
      .listVehicleBrandsByFilter({
        page: vehicleBrandsData.page,
        limit: vehicleBrandsData.limit,
        brandIds: filterBrandsTabIds,
        ...(vehicleBrandsSearch ? { search: vehicleBrandsSearch } : {}),
      })
      .then((response) => {
        if (cancelled) return;

        if ([200, 201].includes(response?.status)) {
          const raw =
            response?.data?.data ?? response?.data?.vehicleBrands ?? [];
          const rows = Array.isArray(raw) ? raw : [];
          setVehicleBrandsData((prev) => ({
            ...prev,
            data: rows,
            count: response?.data?.total ?? rows.length,
            isLoading: false,
          }));
        } else {
          notify(
            response?.message ??
            response?.data?.message ??
            'Error al cargar marcas de vehículo',
            'error'
          );
          setVehicleBrandsData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          notify('Error al cargar marcas de vehículo', 'error');
          setVehicleBrandsData((prev) => ({ ...prev, isLoading: false }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    vehicleSubTab,
    vehicleBrandsData.page,
    vehicleBrandsData.limit,
    vehicleBrandsListVersion,
    vehicleBrandsSearch,
    filterBrandsTabIds,
  ]);

  useEffect(() => {
    if (vehicleSubTab !== 'modelos') {
      return undefined;
    }

    let cancelled = false;

    vehicleService
      .listVehicleBrands({ page: 1, limit: 100000 })
      .then((response) => {
        if (cancelled) return;
        if ([200, 201].includes(response?.status)) {
          const raw =
            response?.data?.data ?? response?.data?.vehicleBrands ?? [];
          setVehicleBrandsForSelect(Array.isArray(raw) ? raw : []);
        } else {
          notify(
            response?.message ??
            response?.data?.message ??
            'Error al cargar marcas de vehículo',
            'error'
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          notify('Error al cargar marcas de vehículo', 'error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [vehicleSubTab, vehicleBrandsListVersion]);

  useEffect(() => {
    if (vehicleSubTab !== 'modelos') {
      return undefined;
    }

    let cancelled = false;

    setVehicleModelsData((prev) => ({ ...prev, isLoading: true }));

    vehicleService
      .listVehicleModelsByFilter({
        page: vehicleModelsData.page,
        limit: vehicleModelsData.limit,
        brandIds: filterBrandIds,
        modelIds: filterModelIds,
        ...(vehicleModelsSearch ? { search: vehicleModelsSearch } : {}),
      })
      .then((response) => {
        if (cancelled) return;

        if ([200, 201].includes(response?.status)) {
          const raw =
            response?.data?.data ?? response?.data?.vehicleModels ?? [];
          const rows = Array.isArray(raw) ? raw : [];
          setVehicleModelsData((prev) => ({
            ...prev,
            data: rows,
            count: response?.data?.total ?? rows.length,
            isLoading: false,
          }));
        } else {
          notify(
            response?.message ??
            response?.data?.message ??
            'Error al cargar modelos de vehículo',
            'error'
          );
          setVehicleModelsData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          notify('Error al cargar modelos de vehículo', 'error');
          setVehicleModelsData((prev) => ({ ...prev, isLoading: false }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    vehicleSubTab,
    vehicleModelsData.page,
    vehicleModelsData.limit,
    vehicleModelsListVersion,
    vehicleModelsSearch,
    filterBrandIds,
    filterModelIds,
  ]);

  useEffect(() => {
    if (vehicleSubTab !== 'tipos') {
      return undefined;
    }

    let cancelled = false;

    setVehicleTypesData((prev) => ({ ...prev, isLoading: true }));

    vehicleService
      .listVehicleTypesByFilter({
        page: vehicleTypesData.page,
        limit: vehicleTypesData.limit,
        typesIds: filterTypesIds,
        classesIds: filterClassesIds,
        ...(vehicleTypesSearch ? { search: vehicleTypesSearch } : {}),
      })
      .then((response) => {
        if (cancelled) return;

        if ([200, 201].includes(response?.status)) {
          const rows = response?.data?.data ?? [];
          setVehicleTypesData((prev) => ({
            ...prev,
            data: rows.map(mapVehicleTypeFromApi),
            count: response?.data?.total,
            isLoading: false,
          }));
        } else {
          notify(
            response?.message ??
            response?.data?.message ??
            'Error al cargar tipos de vehículo',
            'error'
          );
          setVehicleTypesData((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      })
      .catch(() => {
        if (cancelled) return;
        notify('Error al cargar tipos de vehículo', 'error');
        setVehicleTypesData((prev) => ({ ...prev, isLoading: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [
    vehicleSubTab,
    vehicleTypesData.page,
    vehicleTypesData.limit,
    vehicleTypesListVersion,
    vehicleTypesSearch,
    filterTypesIds,
    filterClassesIds,
  ]);

  return (
    <>
      <div className="parameters-page__pills-row">
        <div className="parameters-page__pills">
          <ActionButton
            action={() => setVehicleSubTab('tipos')}
            label="Tipos"
          />
          <ActionButton
            action={() => setVehicleSubTab('marcas')}
            label="Marcas"
          />
          <ActionButton
            action={() => setVehicleSubTab('modelos')}
            label="Modelos"
          />
        </div>
        <ActionButton
          action={() => {
            if (vehicleSubTab === 'tipos') {
              openCreateVehicleTypeForm();
            } else if (vehicleSubTab === 'marcas') {
              openCreateVehicleBrandForm();
            } else if (vehicleSubTab === 'modelos') {
              openCreateVehicleModelForm();
            }
          }}
          label={
            vehicleSubTab === 'tipos'
              ? '+ Crear tipo'
              : vehicleSubTab === 'marcas'
                ? '+ Crear marca'
                : '+ Crear modelo'
          }
        />
      </div>

      {((vehicleSubTab !== 'tipos' || showVehicleTypeForm) &&
        (vehicleSubTab !== 'marcas' || showVehicleBrandForm) &&
        (vehicleSubTab !== 'modelos' || showVehicleModelForm)) && (
          <div className="parameters-page__create-panel">
            {vehicleSubTab === 'tipos' && showVehicleTypeForm && (
              <VehicleTypeFormPanel
                editingVehicleTypeId={editingVehicleTypeId}
                vehicleClassOptionsList={vehicleClassOptionsList}
                register={registerVehicleType}
                errors={vehicleTypeErrors}
                onSubmit={onSubmitVehicleTypeForm}
                onCancel={cancelEditVehicleType}
              />
            )}
            {vehicleSubTab === 'marcas' && showVehicleBrandForm && (
              <VehicleBrandFormPanel
                editingVehicleBrandId={editingVehicleBrandId}
                vehicleClassOptionsList={vehicleClassOptionsList}
                register={registerVehicleBrand}
                errors={vehicleBrandErrors}
                onSubmit={onSubmitVehicleBrandForm}
                onCancel={cancelVehicleBrandForm}
              />
            )}
            {vehicleSubTab === 'modelos' && showVehicleModelForm && (
              <VehicleModelFormPanel
                editingVehicleModelId={editingVehicleModelId}
                brandOptions={vehicleBrandSelectOptionsForModels}
                register={registerVehicleModel}
                errors={vehicleModelErrors}
                onSubmit={onSubmitVehicleModelForm}
                onCancel={cancelVehicleModelForm}
              />
            )}
          </div>
        )}

      {vehicleSubTab === 'tipos' && (
        <>
          <div className="parameters-page__table-toolbar">
            <SearchInput
              placeholder="Buscar por tipo"
              width="100%"
              value={vehicleTypesSearchInput}
              onChange={(e) => setVehicleTypesSearchInput(e.target.value)}
              searchAction={() => {}}
            />
          </div>
          <VehicularTypesTable
            header={vehicularTypesHeaderWithFilter}
            data={vehicleTypesData}
            handlePageChange={handleVehicleTypesPageChange}
            onToggleActive={handleToggleVehicleTypeActive}
            onEdit={handleEditVehicleType}
            itemsPerPageOptions={itemsPerPageOptions}
            onPerPageChange={onVehicleTypesSelectPerPage}
            inactiveCount={inactiveVehicleTypes}
          />
        </>
      )}
      {vehicleSubTab === 'marcas' && (
        <>
          <div className="parameters-page__table-toolbar">
            <SearchInput
              placeholder="Buscar por marca"
              width="100%"
              value={vehicleBrandsSearchInput}
              onChange={(e) => setVehicleBrandsSearchInput(e.target.value)}
              searchAction={() => {}}
            />
          </div>
          <VehicularBrandsTable
            header={vehicularBrandsHeaderWithFilter}
            data={vehicleBrandsTable}
            handlePageChange={handleVehicleBrandsPageChange}
            onToggleActive={handleToggleVehicleBrandActive}
            onEdit={handleEditVehicleBrand}
            childLabel="modelos"
            itemsPerPageOptions={itemsPerPageOptions}
            onPerPageChange={onVehicleBrandsSelectPerPage}
            inactiveCount={inactiveVehicleBrands}
          />
        </>
      )}
      {vehicleSubTab === 'modelos' && (
        <>
          <div className="parameters-page__table-toolbar">
            <SearchInput
              placeholder="Buscar por modelo"
              width="100%"
              value={vehicleModelsSearchInput}
              onChange={(e) => setVehicleModelsSearchInput(e.target.value)}
              searchAction={() => {}}
            />
          </div>
          <VehicularModelsTable
            header={vehicularModelsHeaderWithFilter}
            data={vehicleModelsTable}
            handlePageChange={handleVehicleModelsPageChange}
            onToggleActive={handleToggleVehicleModelActive}
            onEdit={handleEditVehicleModel}
            itemsPerPageOptions={itemsPerPageOptions}
            onPerPageChange={onVehicleModelsSelectPerPage}
            inactiveCount={inactiveVehicleModels}
          />
        </>
      )}
    </>
  );
};
